import { RiDeleteBinLine, RiDownload2Line } from "@remixicon/react";
import { FileThumbCard } from "./styled";
import { Button, Spinner } from "reactstrap";
import { ISource } from "../../../../services";
import { useState } from "react";
import useFileIcon from "./useFileIcon";
import { useDeleteDialog } from "../../../../context/useDeleteDialog";

interface FileThumbsProps {
  file: ISource;
  accessToDelete: boolean;
  accessToDownload?: boolean;
  onDelete: (file: ISource) => Promise<void>;
  onDownload: (file: ISource) => Promise<void>;
  deleteDialogMessage: string;
}

export function FileThumbs({
  file,
  onDownload,
  onDelete,
  accessToDelete,
  accessToDownload,
  deleteDialogMessage,
}: FileThumbsProps) {
  const FileIcon = useFileIcon(file.media_type);
  const isProcessing = !["pending_upload", "processing_complete"].includes(file.processing_status);
  const { onDeleteDialog } = useDeleteDialog();

  const [loading, setLoading] = useState(false);

  function formatName(name: string) {
    const splitName = name.split(".");
    splitName.length > 0 && splitName.pop();
    return splitName.join(" ").trim();
  }

  function formatType(type: string) {
    const mimeMap: { [key: string]: string } = {
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
      "application/pdf": "pdf",
      "text/plain": "txt",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
      "application/vnd.ms-powerpoint": "ppt",
      "text/csv": "csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
      // Adicione mais tipos MIME conforme necessário
    };

    return mimeMap[type] || "unknown";
  }

  function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const handleDelete = async () => {
    setLoading(true);
    await onDelete(file);
    setLoading(false);
  };

  const handleDownload = () => {
    onDownload(file);
  };

  return (
    <FileThumbCard loading={loading}>
      <div className="file-icon-container d-none d-sm-flex">
        {isProcessing ? (
          <Spinner
            size="sm"
            className="button-icon flex-shrink-0 align-middle fs-16"
            color="secondary"
          />
        ) : (
          <FileIcon className="drop-icon" />
        )}
      </div>
      <div className="file-info-container">
        <h6>{formatName(file.file_name)}</h6>

        <p>
          <strong>.{formatType(file.media_type)}</strong> {formatBytes(file.size_in_bytes)}
          {isProcessing ? (
            <span className="badge border border-secondary text-secondary mx-2">Processando</span>
          ) : null}
        </p>
      </div>
      <div className="action-btn-container">
        {file.file_path ? (
          <Button
            className="btn btn-icon"
            color="light"
            size="sm"
            type="button"
            style={{ backgroundColor: "white" }}
            onClick={handleDownload}
            disabled={!accessToDownload}
          >
            <RiDownload2Line style={{ width: "18px", height: "18px" }} />
          </Button>
        ) : null}
        <Button
          className="btn btn-icon"
          color="light"
          size="sm"
          type="button"
          style={{ backgroundColor: "white" }}
          onClick={() =>
            onDeleteDialog({
              callback: handleDelete,
              message: deleteDialogMessage,
            })
          }
          disabled={!accessToDelete}
        >
          <RiDeleteBinLine style={{ width: "18px", height: "18px" }} />
        </Button>
      </div>
    </FileThumbCard>
  );
}
